import React, { useEffect, useState, useContext } from "react";
import {
  Checkbox,
  Box,
  Button,
  IconButton,
  Modal,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Reorder } from "framer-motion";
import {
  ArrowsClockwise,
  Cards,
  CaretDown,
  Check,
  CheckSquareOffset,
  CirclesThreePlus,
  Clock,
  Cube,
  DotsSixVertical,
  FilmSlate,
  FilmStrip,
  FolderStar,
  PencilSimple,
  Plus,
  PushPin,
  PushPinSimple,
  Trash,
  X,
} from "phosphor-react";
import { fpatch, fpost } from "../../../../API/callsAPI";
import { MovieColabContext } from "../../../../Context/MovieColabContext";
import CustomDropdown from "../../../Utilities/CustomDropdown/CustomDropdown.component";
import { EpisodesIconCustom } from "../../../Utilities/Svg/EpisodesIcon";
import { ProjectMediaIconCustom } from "../../../Utilities/Svg/ProjectMediaIcon";
import { ReviewerIcon } from "../../../Utilities/Svg/Reviewer";
import useStyles from "./NavigationSettingsCommon.styles";
import NavigationSettingsCommon from "./NavigationSettingsCommon";
import {
  buildConfigItems,
  buildPageGroupFromSelection,
  parseNavConfigItems,
} from "./navigationSettingsUtils";

const MyNavigation = ({ setSnackValue, projectId }) => {
  const classes = useStyles();
  const { loggedInUser, navConfig, getNavConfig } =
    useContext(MovieColabContext);
  const [pages, setPages] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [navGroups, setNavGroups] = useState([]);
  const [navOrder, setNavOrder] = useState([]);
  const [showPageNames, setShowPageNames] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCustomPageModalOpen, setIsCustomPageModalOpen] = useState(false);
  const [pageGroupModal, setPageGroupModal] = useState({
    open: false,
    editData: null,
  });

  const hydrateFromConfig = (config) => {
    const items = config?.config?.items || [];
    const parsedItems = parseNavConfigItems(items);
    setPages(parsedItems.pages);
    setCustomPages(parsedItems.customPages);
    setNavGroups(parsedItems.navGroups);
    setNavOrder(parsedItems.navOrder);
    setShowPageNames(config?.config?.show_page_names ?? true);
    setIsDirty(false);
  };

  const buildNavConfigPayload = (items) => ({
    project: projectId,
    user: loggedInUser.id,
    config: {
      items,
      show_page_names: showPageNames,
    },
  });

  const saveNavConfig = async (items) => {
    if (!navConfig || !loggedInUser?.id) {
      return;
    }

    setIsSaving(true);

    try {
      const data = buildNavConfigPayload(items);

      if (navConfig.user == null) {
        await fpost({
          url: `/project/${projectId}/navbar-configs/`,
          data,
        });
      } else {
        await fpatch({
          url: `/project/${projectId}/navbar-config/${navConfig.id}/`,
          data,
        });
      }

      setSnackValue({
        isOpen: true,
        message: "Changes saved successfully",
        isError: false,
      });
      hydrateFromConfig(await getNavConfig(projectId));
    } catch (error) {
      setSnackValue({
        isOpen: true,
        message: `${error}`,
        isError: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      return;
    }

    getNavConfig(projectId).then((config) => {
      if (config) {
        hydrateFromConfig(config);
      }
    });
  }, [projectId, getNavConfig]);

  const handlePageToggle = (pageId) => {
    setIsDirty(true);
    setPages((prevPages) => {
      const updatedPages = prevPages.map((page) =>
        page.key === pageId ? { ...page, enabled: !page.enabled } : page,
      );

      setNavOrder((prevOrder) => {
        const toggledPage = updatedPages.find((page) => page.key === pageId);

        if (!toggledPage.enabled) {
          return prevOrder.filter((item) => item.key !== pageId);
        }

        if (prevOrder.some((item) => item.key === pageId)) {
          return prevOrder;
        }

        return [...prevOrder, toggledPage];
      });

      return updatedPages;
    });
  };

  const handleCustomPageToggle = (pageId) => {
    setIsDirty(true);
    setCustomPages((prevPages) => {
      const updatedPages = prevPages.map((page) =>
        page.key === pageId ? { ...page, enabled: !page.enabled } : page,
      );

      setNavOrder((prevOrder) => {
        const toggledPage = updatedPages.find((page) => page.key === pageId);

        if (!toggledPage.enabled) {
          return prevOrder.filter((item) => item.key !== pageId);
        }

        if (prevOrder.some((item) => item.key === pageId)) {
          return prevOrder;
        }

        return [...prevOrder, toggledPage];
      });

      return updatedPages;
    });
  };

  const handleGroupToggle = (groupKey) => {
    setIsDirty(true);
    setNavGroups((prevGroups) => {
      const updatedGroups = prevGroups.map((group) =>
        group.key === groupKey ? { ...group, enabled: !group.enabled } : group,
      );

      setNavOrder((prevOrder) => {
        const toggledGroup = updatedGroups.find(
          (group) => group.key === groupKey,
        );

        if (!toggledGroup.enabled) {
          return prevOrder.filter((item) => item.key !== groupKey);
        }

        if (prevOrder.some((item) => item.key === groupKey)) {
          return prevOrder;
        }

        return [...prevOrder, toggledGroup];
      });

      return updatedGroups;
    });
  };

  //
  const handleNavReorder = (newOrder) => {
    setIsDirty(true);
    setNavOrder(newOrder);
  };

  const handleDiscardChanges = () => {
    hydrateFromConfig(navConfig);
  };

  const handleSaveChanges = async () => {
    await saveNavConfig(
      buildConfigItems(navOrder, pages, customPages, navGroups),
    );
  };

  const handleAddCustomPage = async (selectedPages) => {
    const newCustomPages = selectedPages.map((page) => ({
      key: page.id,
      label: page.name,
      enabled: true,
      type: "customPage",
    }));

    await saveNavConfig(
      buildConfigItems(
        navOrder,
        pages,
        [...customPages, ...newCustomPages],
        navGroups,
      ),
    );
  };

  const handleDeleteCustomPage = async (pageId) => {
    setIsDirty(true);
    const updatedCustomPages = customPages.filter(
      (page) => page.key !== pageId,
    );
    const newNavOrder = navOrder.filter((item) => item.key !== pageId);

    await saveNavConfig(
      buildConfigItems(newNavOrder, pages, updatedCustomPages, navGroups),
    );
  };

  const handleAddPageGroup = async ({ name, iconId, pageIds }) => {
    const newPageGroup = buildPageGroupFromSelection(
      name,
      iconId,
      pageIds,
      true,
      pages,
      customPages,
    );
    const updatedNavOrder = [...navOrder, newPageGroup];

    await saveNavConfig(
      buildConfigItems(updatedNavOrder, pages, customPages, [
        ...navGroups,
        newPageGroup,
      ]),
    );
  };

  const handleEditPageGroup = async ({ name, iconId, pageIds, groupKey }) => {
    const existingGroup = navGroups.find((group) => group.key === groupKey);

    if (!existingGroup) {
      return;
    }

    const updatedPageGroup = buildPageGroupFromSelection(
      name,
      iconId,
      pageIds,
      existingGroup.enabled,
      pages,
      customPages,
    );

    const updatedNavGroups = navGroups.map((group) =>
      group.key === groupKey ? updatedPageGroup : group,
    );

    const updatedNavOrder = navOrder.some((item) => item.key === groupKey)
      ? navOrder.map((item) =>
          item.key === groupKey ? updatedPageGroup : item,
        )
      : navOrder;

    await saveNavConfig(
      buildConfigItems(updatedNavOrder, pages, customPages, updatedNavGroups),
    );
  };

  const handleDeletePageGroup = async (groupKey) => {
    setIsDirty(true);
    const updatedNavGroups = navGroups.filter(
      (group) => group.key !== groupKey,
    );
    const newNavOrder = navOrder.filter((item) => item.key !== groupKey);

    await saveNavConfig(
      buildConfigItems(newNavOrder, pages, customPages, updatedNavGroups),
    );
  };

  const handleResetToProjectDefault = async () => {
    try {
      await fpost({
        url: `/project/${projectId}/user-navbar-config/reset/`,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSnackValue({
        isOpen: true,
        message: "Changes reset successfully",
        isError: false,
      });
      hydrateFromConfig(await getNavConfig(projectId));
    }
  };

  return (
    <>
      <div className={classes.headerRow}>
        <Typography className={classes.subtitle}>
          Your navigation is based on your project's default, but any changes
          you make here are yours to keep.{" "}
          <Button
            onClick={handleResetToProjectDefault}
            startIcon={<ArrowsClockwise />}
            sx={{
              fontSize: "14px !important",
              textTransform: "none",
              color: "#B1B5C9",
              textDecoration: "underline",
            }}
          >
            Reset to project default
          </Button>
        </Typography>
        <div className={classes.headerActions}>
          <Button
            variant="contained"
            startIcon={<Check />}
            className={classes.saveButton}
            onClick={handleSaveChanges}
            disabled={!isDirty || isSaving}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            startIcon={<X />}
            className={classes.discardButton}
            onClick={handleDiscardChanges}
            disabled={!isDirty || isSaving}
          >
            Discard Changes
          </Button>
        </div>
      </div>
      <NavigationSettingsCommon
        pages={pages}
        customPages={customPages}
        navGroups={navGroups}
        navOrder={navOrder}
        showPageNames={showPageNames}
        setShowPageNames={(value) => {
          setIsDirty(true);
          setShowPageNames(value);
        }}
        setSnackValue={setSnackValue}
        projectId={projectId}
        handlePageToggle={handlePageToggle}
        handleCustomPageToggle={handleCustomPageToggle}
        handleGroupToggle={handleGroupToggle}
        handleNavReorder={handleNavReorder}
        handleAddCustomPage={handleAddCustomPage}
        handleDeleteCustomPage={handleDeleteCustomPage}
        handleAddPageGroup={handleAddPageGroup}
        handleEditPageGroup={handleEditPageGroup}
        handleDeletePageGroup={handleDeletePageGroup}
      />
    </>
  );
};

export default MyNavigation;
